import {
    Avatar,
    Box,
    Button,
    Flex,
    FormControl,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,

    Text,
    useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import MaterialTable, { Column } from "material-table";
import { FaSchool } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { GrEdit } from "react-icons/gr";
import { FiSearch } from "react-icons/fi";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { School } from "../../interfaes/school";
import { User, Roles } from "../../interfaes/user";

import { onDeleteSchools, onGetSchools } from "../../http/school";
import CoustomAlertDialog from "../../components/CoustomAlertDialog";
import SearchableSelect from "../../components/SearchableSelect";
import ModernLoader from "../../components/ModernLoader";
import { isAdmin } from "../../utils/common";
import TablePageShell, { SkeletonTableLoader, BrandButton } from "../../components/TablePageShell";


export default function SchoolPage() {
    const { isOpen, onOpen, onClose } = useDisclosure()

    const { t } = useTranslation('school');
    const navigate = useNavigate();
    const [data, setData] = useState<School[]>([]);
    const [deleteIndex, setDeleteIndex] = useState("");

    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedProvince, setSelectedProvince] = useState("");
    const [selectedDistrict, setSelectedDistrict] = useState("");

    const handleSchoolNameClick = (school: School) => {
        if (!school.id) return;
        navigate(`/school-details/${school.id}`, { state: school });
    };

    const provinces = useMemo(() => Array.from(new Set(data.map((s) => s.province).filter(Boolean))).sort(), [data]);
    const districts = useMemo(() => {
        const from = selectedProvince
            ? data.filter((s) => s.province === selectedProvince).map((s) => s.district)
            : data.map((s) => s.district);
        return Array.from(new Set(from.filter(Boolean))).sort();
    }, [data, selectedProvince]);

    const filteredData = useMemo(() => {
        return data.filter((row) => {
            const matchSearch = !searchTerm || [row.name, row.province, row.district, row.localAddress, row.localLevel]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(searchTerm.toLowerCase());
            const matchProvince = !selectedProvince || row.province === selectedProvince;
            const matchDistrict = !selectedDistrict || row.district === selectedDistrict;
            return matchSearch && matchProvince && matchDistrict;
        });
    }, [data, searchTerm, selectedProvince, selectedDistrict]);

    const columns: Column<School>[] = [
        { title: t('id'), field: 'id', type: 'numeric', hidden: true },
        { title: t('schoolName'), field: 'name' },
        {
            title: t('studentsCount'),
            field: 'studentCount',
            render: (rowData) => rowData.studentCount ?? 0,
        },
        { title: t('province'), field: 'province' },
        { title: t('district'), field: 'district' },
        {
            title: t('address'),
            field: 'localAddress',
            render: (rowData) => {
                const parts = [rowData.localLevel, rowData.localAddress].filter(Boolean);
                return parts.length ? parts.join(', ') : '-';
            },
        },
    ];
    const defaultMaterialTheme = createTheme();


    useEffect(() => {
        getSchools();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSchools = async () => {
        try {
            setLoading(true);
            const response = await onGetSchools();
            setData(response.data.items);
        } catch (e) {
            console.log(e)
        } finally {
            setLoading(false);
        }
    }
    const onDeleteSchool = async () => {
        try {
            setLoading(true)
            await onDeleteSchools(deleteIndex);
            const arrayOfObjects = data.filter((obj: School) => obj.id !== deleteIndex);
            setData(arrayOfObjects);

        } catch (e) {
            console.log(e)
        } finally {
            handleClose();
        }
    }

    const handleClose = () => {
        setDeleteIndex('');
        setLoading(false)
        onClose();

    }


    if (loading) {
        return <SkeletonTableLoader rows={8} showFilters={true} />;
    }

    return <TablePageShell
        title={t('schoolsList')}
        subtitle={`${filteredData.length} school${filteredData.length !== 1 ? 's' : ''} found`}
        actions={
            <BrandButton
                leftIcon={<FaSchool size={15} />}
                onClick={() => navigate("/school/add")}
            >
                {t('addSchool')}
            </BrandButton>
        }
        filters={<>
            <FormControl flex={{ base: '1 1 100%', md: '1 1 240px' }} maxW={{ base: '100%', md: '280px' }}>
                <InputGroup>
                    <InputLeftElement pointerEvents="none" color="gray.400" h="100%">
                        <FiSearch size={16} />
                    </InputLeftElement>
                    <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.200"
                        placeholder="Search schools..."
                        fontSize="13px"
                    />
                </InputGroup>
            </FormControl>
            <FormControl flex={{ base: '1 1 100%', sm: '1 1 160px' }} maxW={{ base: '100%', sm: '200px' }}>
                <SearchableSelect
                    options={provinces.map((p) => ({ label: p, value: p }))}
                    value={selectedProvince}
                    onChange={(e: any) => { setSelectedProvince(e.target.value || ''); setSelectedDistrict(''); }}
                    label={t('selectProvince')}
                />
            </FormControl>
            <FormControl flex={{ base: '1 1 100%', sm: '1 1 160px' }} maxW={{ base: '100%', sm: '200px' }}>
                <SearchableSelect
                    options={districts.map((d) => ({ label: d, value: d }))}
                    value={selectedDistrict}
                    onChange={(e: any) => setSelectedDistrict(e.target.value || '')}
                    isDisabled={!selectedProvince && districts.length === 0}
                    label={t('selectDistrict')}
                />
            </FormControl>
        </>}
    >
            <ThemeProvider theme={defaultMaterialTheme}>
                <MaterialTable
                    title=""
                    columns={columns}
                    data={filteredData}
                    isLoading={loading}
                    onRowClick={(_, rowData) => rowData && handleSchoolNameClick(rowData)}
                    options={{
                        search: false,
                        sorting: true,
                        actionsColumnIndex: -1,
                        pageSize: 20,
                        paginationPosition: 'both'
                    }}
                    actions={[
                        isAdmin() && {
                            icon: 'edit',
                            tooltip: 'Edit School',
                            onClick: (event: any, rowData: any) => {
                                navigate("/school/" + rowData.id, { state: rowData })
                            }
                        },
                        isAdmin() && {
                            icon: 'delete',
                            tooltip: 'Delete School',
                            onClick: (event: any, rowData: any) => {
                                setDeleteIndex(rowData.id);
                                onOpen();
                            }
                        }
                    ].filter(Boolean) as any}
                    components={{
                        Action: props => (
                            <div style={{ display: 'flex', flexDirection: 'row' }} onClick={(e) => e.stopPropagation()}>
                                <IconButton
                                    background={'#eeeeee'}
                                    p={"10px"}
                                    borderRadius={"10px"}
                                    aria-label={props.action.tooltip}
                                    mr={"12px"}
                                    onClick={() => props.action.onClick(props.data)}
                                    icon={props.action.icon === 'edit' ? <GrEdit size="20px" color={"green"} /> :
                                        <MdDelete size="20px" color={"red"} />}
                                />
                            </div>
                        ),
                        OverlayLoading: (props) => (
                            <Box
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                zIndex={100}
                                bg="whiteAlpha.700"
                                display={loading ? "flex" : "none"}
                                alignItems="flex-start"
                                justifyContent="center"
                                pt="80px"
                            >
                                <ModernLoader h="150px" message="Fetching schools..." />
                            </Box>
                        )
                    }}
                />
            </ThemeProvider>

        <CoustomAlertDialog
            onClose={handleClose}
            onOpen={onOpen}
            isOpen={isOpen}
            onMainAction={onDeleteSchool}
            loading={loading}
        />
    </TablePageShell>
}
